import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowDown,
  ArrowUp,
  Activity,
  Workflow,
  Sparkles,
  Building2,
  Stethoscope,
  MapPin,
  Users,
} from "lucide-react";

const REACH = [
  { icon: Building2, value: "15", label: "Hospitals" },
  { icon: Stethoscope, value: "250+", label: "Clinics" },
  { icon: MapPin, value: "6", label: "States" },
  { icon: Users, value: "15M+", label: "Patients" },
];

const RESULTS = [
  { direction: "down", value: "25%", label: "IT Operational Cost" },
  { direction: "up", value: "41%", label: "Workflow Efficiency" },
  { direction: "down", value: "54%", label: "Manual Workload" },
];

const FLOW_STEPS = [
  { label: "Analyze systems", icon: Activity },
  { label: "Optimize workflows", icon: Workflow },
  { label: "Deliver outcomes", icon: Sparkles },
];

function HeroProofPanel() {
  const reduceMotion = useReducedMotion();
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const id = window.setInterval(() => {
      setActiveStep((s) => (s + 1) % FLOW_STEPS.length);
    }, 2200);
    return () => window.clearInterval(id);
  }, [reduceMotion]);

  return (
    <div className="w-full max-w-[26rem] lg:mx-auto">
      <div className="relative overflow-hidden rounded-[1.1rem] border border-white/10 bg-[#0b1620]/80 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.55)] backdrop-blur-md">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-white/[0.03] px-5 py-3 sm:px-6">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-burnt-sienna-300/95">
              Velozent Impact
            </p>
            <p className="mt-0.5 truncate text-[0.78rem] text-white/55">
              Across health systems we serve
            </p>
          </div>
          <div className="flex flex-col items-end shrink-0">
            <span className="font-mono text-[1.1rem] font-bold tabular-nums text-burnt-sienna-200">
              $15M
            </span>
            <span className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-white/45">
              Avg Annual Savings
            </span>
          </div>
        </div>

        <div className="px-5 pb-5 pt-4 sm:px-6 sm:pt-5">
          {/* Reach grid */}
          <ul className="grid grid-cols-4 gap-2">
            {REACH.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.li
                  key={r.label}
                  initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.4,
                    delay: reduceMotion ? 0 : 0.05 + i * 0.07,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="flex flex-col items-center gap-1 rounded-lg border border-white/10 bg-white/[0.04] px-1.5 py-2.5"
                >
                  <Icon
                    className="h-3.5 w-3.5 text-burnt-sienna-300"
                    strokeWidth={2}
                  />
                  <span className="font-mono text-[0.95rem] font-bold tabular-nums text-white">
                    {r.value}
                  </span>
                  <span className="text-[9.5px] font-semibold uppercase tracking-[0.08em] text-white/55">
                    {r.label}
                  </span>
                </motion.li>
              );
            })}
          </ul>

          {/* Divider */}
          <div className="mt-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-[9.5px] font-semibold uppercase tracking-[0.18em] text-white/45">
              Typical Outcomes
            </span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          {/* Execution flow */}
          <div className="mt-4 flex items-center gap-2 text-[11px] font-mono text-white/55">
            {FLOW_STEPS.map((step, i) => {
              const Icon = step.icon;
              const isActive = i === activeStep;
              return (
                <div key={step.label} className="flex items-center gap-2">
                  <motion.div
                    animate={
                      reduceMotion
                        ? {}
                        : {
                            opacity: isActive ? 1 : 0.5,
                            scale: isActive ? 1 : 0.96,
                          }
                    }
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className={`flex items-center gap-1.5 rounded-md border px-2 py-1 ${
                      isActive
                        ? "border-burnt-sienna-400/50 bg-burnt-sienna-500/10 text-burnt-sienna-200"
                        : "border-white/10 bg-white/[0.03] text-white/55"
                    }`}
                  >
                    <Icon className="h-3 w-3" strokeWidth={2} />
                    <span className="hidden sm:inline">{step.label}</span>
                  </motion.div>
                  {i < FLOW_STEPS.length - 1 ? (
                    <span className="text-white/30">→</span>
                  ) : null}
                </div>
              );
            })}
          </div>

          {/* Result rows */}
          <ul className="mt-4 space-y-2.5" aria-label="Typical implementation outcomes">
            {RESULTS.map((r, i) => {
              const Arrow = r.direction === "up" ? ArrowUp : ArrowDown;
              const isPositive = r.direction === "up";
              return (
                <motion.li
                  key={r.label}
                  initial={reduceMotion ? false : { opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.45,
                    delay: reduceMotion ? 0 : 0.2 + i * 0.1,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.04] px-3.5 py-2.5"
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className={`flex h-7 w-7 items-center justify-center rounded-md ${
                        isPositive
                          ? "bg-emerald-500/15 text-emerald-300"
                          : "bg-burnt-sienna-500/15 text-burnt-sienna-200"
                      }`}
                      aria-hidden
                    >
                      <Arrow className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </span>
                    <span className="text-[0.92rem] font-semibold text-white/90">
                      {r.label}
                    </span>
                  </div>
                  <span
                    className={`font-mono text-[1.05rem] font-bold tabular-nums ${
                      isPositive ? "text-emerald-300" : "text-burnt-sienna-200"
                    }`}
                  >
                    {isPositive ? "↑" : "↓"} {r.value}
                  </span>
                </motion.li>
              );
            })}
          </ul>

          <p className="mt-4 text-center text-[10px] font-medium uppercase tracking-[0.14em] text-white/40">
            Based on recent healthcare implementations
          </p>
        </div>
      </div>
    </div>
  );
}

export default HeroProofPanel;
